/**
 * useBetaStatus Hook
 *
 * Checks if user is a beta user and if their beta has expired.
 * Returns beta status information.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface BetaStatus {
  isBetaUser: boolean;
  betaExpired: boolean;
  betaStartDate: string | null;
  betaEndDate: string | null;
  loading: boolean;
}

export function useBetaStatus(): BetaStatus {
  const { user } = useAuth();
  const [betaStatus, setBetaStatus] = useState<BetaStatus>({
    isBetaUser: false,
    betaExpired: false,
    betaStartDate: null,
    betaEndDate: null,
    loading: true
  });

  useEffect(() => {
    if (!user) {
      setBetaStatus({
        isBetaUser: false,
        betaExpired: false,
        betaStartDate: null,
        betaEndDate: null,
        loading: false
      });
      return;
    }

    const checkBetaStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('is_beta_user, beta_expired, beta_start_date, beta_end_date')
          .eq('id', user.id)
          .maybeSingle(); // Use maybeSingle to avoid 406 errors during auth race conditions

        if (error && error.code !== 'PGRST116') {
          logger.error('Error checking beta status:', error);
          throw error;
        }

        // If no profile found or not a beta user
        if (!data || !data.is_beta_user) {
          setBetaStatus({
            isBetaUser: false,
            betaExpired: false,
            betaStartDate: null,
            betaEndDate: null,
            loading: false
          });
          return;
        }

        // Check if beta has expired (client-side check)
        const now = new Date();
        const endDate = data.beta_end_date ? new Date(data.beta_end_date) : null;
        const expired = endDate ? now > endDate : false;

        // If expired but not marked in database, update it
        if (expired && !data.beta_expired) {
          logger.debug('Beta expired, updating database...');
          await supabase
            .from('user_profiles')
            .update({ beta_expired: true })
            .eq('id', user.id);
        }

        setBetaStatus({
          isBetaUser: true,
          betaExpired: expired || data.beta_expired,
          betaStartDate: data.beta_start_date,
          betaEndDate: data.beta_end_date,
          loading: false
        });

        logger.debug('Beta status checked:', {
          isBetaUser: true,
          expired,
          endDate: data.beta_end_date
        });
      } catch (error) {
        logger.error('Error checking beta status:', error);
        setBetaStatus(prev => ({ ...prev, loading: false }));
      }
    };

    checkBetaStatus();

    // Check beta status every 5 minutes
    const interval = setInterval(checkBetaStatus, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  return betaStatus;
}
